import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/services/auth0/session";
import axios from "axios";

export interface V1CustomerListItem {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  customerNumber: number;
  createdAt: string;
  migratedToV2: boolean;
}

export interface V1CustomerListResponse {
  items: V1CustomerListItem[];
  total: number;
}

export interface V1ListOpts {
  status?: "ACTIVE" | "INACTIVE" | "DELETED";
  limit?: number;
  offset?: number;
}

/**
 * Lists v1 customers via the v2 server's admin-protected proxy. Each row
 * carries `migratedToV2` indicating whether v2 already has a Customer row
 * for this email (with legacyAuth0Sub set).
 */
export function useV1Customers(opts: V1ListOpts = {}) {
  const { token } = useSession();
  const { status, limit = 25, offset = 0 } = opts;

  return useQuery({
    queryKey: ["v1-customers", { status, limit, offset }],
    queryFn: async () => {
      const { data } = await axios.get<V1CustomerListResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/migration/v1-customers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status, limit, offset },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

export type InviteSkipReason =
  | "already_migrated"
  | "not_found_in_v1"
  | "exists_in_v2_no_v1_lineage"
  | "v1_lookup_failed"
  | "send_failed";

export interface InviteResponse {
  invited: { email: string }[];
  skipped: { email: string; reason: InviteSkipReason }[];
}

/**
 * Triggers the migration invite for the given emails. Sends an HMAC-signed
 * magic link via SES to each email that passes the pre-flight checks.
 */
export function useInviteMigration() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emails: string[]) => {
      const { data } = await axios.post<InviteResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/migration/invite`,
        { emails },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      // Invited customers won't appear as "migrated" until they click the
      // link, but invalidating the list keeps any other state fresh.
      void queryClient.invalidateQueries({ queryKey: ["v1-customers"] });
    },
  });
}
